/**
 * Format helpers used across Akomoda screens.
 */

export function formatCurrency(amount, currency = "EUR") {
    const n = Number(amount || 0);
    // Format the NUMBER with US/UK style: comma thousands, dot decimals, 2 places.
    const num = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(n);
    // Symbol with a space before the number: "€ 1,000.00"
    const symbol = { EUR: "€", USD: "$", GBP: "£", ALL: "L" }[currency] || currency;
    return `${symbol} ${num}`;
}

export function formatCurrencyPlain(amount, currency = "EUR") {
    // Same number format but without the symbol — useful in tables where the
    // currency is shown separately.
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(n);
}

export function formatDate(iso) {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return iso;
    // European DD/MM/YYYY
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatShortDate(iso) {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return iso;
    // European DD/MM (no year, no month name)
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
}

export function formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // European DD/MM/YYYY HH:mm (24h)
    return d.toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
}

export function formatTime(iso) {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return iso;
    // 24h HH:mm
    return d.toLocaleTimeString("en-GB", {
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
}

export function relativeTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(iso);
}

export function formatLongDate(iso, lang = "en") {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return String(iso);
    if (lang.startsWith("sq")) {
        const days = ["E diel", "E hënë", "E martë", "E mërkurë", "E enjte", "E premte", "E shtunë"];
        const months = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
                        "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
        return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    }
    return d.toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
}

export function parseYMDLocal(ymd) {
    if (!ymd || typeof ymd !== "string") return null;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);         // local midnight — no UTC shift
}

export function toYMD(date) {
    const d = typeof date === "string" ? new Date(date) : date;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

export function nightsBetween(from, to) {
    const a = new Date(from);
    const b = new Date(to);
    return Math.max(0, Math.round((b - a) / 86400000));
}
